import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../hooks';
import { useConfirmation } from '../../../utils/ConfirmationServiceContext/confirmationContext';
import BigFloatButton from '../../BigFloatButton/BigFloatButton';
import Loader from '../../Loader/Loader';
import { Page } from '../../Page/Page';
import {
  STitle,
  SDescriptionText,
  SButtonContainer,
  SStepContainer,
  SStepCircle,
  SStepContentContainer,
  SStepNumber,
  SStepText,
  SStepsContainer,
  SFullScreenContainer,
  SActionButtonsContainer,
  SApproveButton,
  SDeclineButton,
  HomeButton,
} from './TestAlarm.styles';
import {
  getIsLoading,
  getLastReceivedTestId,
  getTestAlarmSentAt,
  getTestId,
} from './testAlarmSlice';
import { reportTestAlarmResult, startTestAlarm } from './testAlarmSlice/actionCreators';
import InfoBanner from '../../InfoBanner/InfoBanner';
import { ReactComponent as PhoneWarningIcon } from '../../../assets/icons/phone-warning-white.svg';
import { ReactComponent as SirenIcon } from '../../../assets/icons/siren-white.svg';
import { ReactComponent as CogIcon } from '../../../assets/icons/cog-white.svg';
import { Button } from '../../Button/Button';
import { useNavigate } from 'react-router-dom';
import { SUPPORT_URL } from '../../../config/app';
import { useLayoutContext } from '../../../utils/customHooks/LayoutContext';
import { useIsSilentMode } from '../../../utils/customHooks/useIsSilentMode';
import { useActiveBlockingAlarm } from '../../../utils/customHooks/useActiveBlockingAlarm';
import { SilentModeBlockedView } from './SilentModeBlockedView';

const TEST_ALARM_DELAY_MS = 15000;
type ActiveView = 'INITIALIZATION' | 'ACTIVE' | 'FINISHED' | 'NOT_RECEIVED';
export const TestAlarm = () => {
  const confirm = useConfirmation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<ActiveView>('INITIALIZATION');
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const isLoading = useAppSelector(getIsLoading);
  const testId = useAppSelector(getTestId);
  const lastReceivedTestId = useAppSelector(getLastReceivedTestId);
  const timestampSent = useAppSelector(getTestAlarmSentAt);
  const layout = useLayoutContext();
  const isSilentMode = useIsSilentMode();
  const blockingAlarm = useActiveBlockingAlarm();

  const testAlarmSteps = [
    'sidebar_settings_testModalDescription_2',
    'sidebar_settings_testModalDescription_3',
    'sidebar_settings_testModalDescription_4',
    'sidebar_settings_testModalDescription_5',
  ];

  useEffect(() => {
    onInitialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeView !== 'INITIALIZATION') {
      layout.setTabsState(false);
      layout.setShowHeader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  useEffect(() => {
    if (activeView !== 'ACTIVE') return undefined;

    const countdownInterval = setInterval(() => {
      setRemainingSeconds(curSec => Math.max(0, curSec - 1));
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [activeView]);

  useEffect(() => {
    if (activeView === 'ACTIVE' && testId && testId === lastReceivedTestId) {
      setActiveView('FINISHED');
    }
  }, [activeView, testId, lastReceivedTestId]);

  const onInitialLoad = () => {
    if (!testId) return;

    if (testId === lastReceivedTestId) {
      setActiveView('FINISHED');
    } else if (testId !== lastReceivedTestId) {
      setActiveView('ACTIVE');
      const currentTimestamp = new Date().getTime();
      const remainingTimeMs = TEST_ALARM_DELAY_MS - (currentTimestamp - Number(timestampSent));
      const remainingTimeSec = remainingTimeMs > 0 ? Math.round(remainingTimeMs / 1000) : 0;
      setRemainingSeconds(remainingTimeSec);
    }
  };

  const handleStartTestAlarm = () => {
    dispatch(startTestAlarm());
    setActiveView('ACTIVE');
    setRemainingSeconds(TEST_ALARM_DELAY_MS / 1000);
    setTimeout(() => {
      setActiveView('FINISHED');
    }, TEST_ALARM_DELAY_MS + 7000);
  };

  const alarmModal = () => {
    confirm({
      title: 'warning',
      description: 'alarm_test_description',
      onSubmit: () => handleStartTestAlarm(),
      onCancel: () => {},
      confirmText: 'alarm_test_trigger',
      cancelText: 'cancel',
    });
  };

  const navigateToDashboard = () => navigate('/dashboard');

  const reportAlarmTest = async (success: boolean) => {
    dispatch(reportTestAlarmResult(success, testId));
  };

  const handleApprove = async () => {
    await reportAlarmTest(true);
    navigateToDashboard();
  };
  const handleDecline = async () => {
    setActiveView('NOT_RECEIVED');
    dispatch(reportTestAlarmResult(false, testId));
  };

  const handleTroubleshoot = () => {
    window.electronApi?.sendOpenLink(SUPPORT_URL);
    navigateToDashboard();
  };

  const renderStepContainer = (stepNumber: number, tx: string, isLastStep = false) => {
    return (
      <SStepContainer key={stepNumber} className={isLastStep ? 'last-step' : ''}>
        <SStepCircle />
        <SStepContentContainer>
          <SStepNumber>{`${t('alarm_step')} ${stepNumber}`}</SStepNumber>
          <SStepText>{t(tx)}</SStepText>
        </SStepContentContainer>
      </SStepContainer>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Page>
      {activeView === 'INITIALIZATION' && (
        <>
          {isSilentMode && blockingAlarm ? (
            <SilentModeBlockedView alarmName={blockingAlarm.emergencyTypeName} />
          ) : (
            <>
              <InfoBanner descriptionTx="test_alarm_info" />
              <SStepsContainer>
                {testAlarmSteps
                  .slice(0, testAlarmSteps.length - 1)
                  .map((tx, index) => renderStepContainer(index + 1, tx))}
                {renderStepContainer(
                  testAlarmSteps.length,
                  testAlarmSteps[testAlarmSteps.length - 1],
                  true
                )}
              </SStepsContainer>
            </>
          )}
          <SButtonContainer role="buttonContainer">
            <BigFloatButton
              className="test-alarm-btn"
              tx="alarm_test_button"
              disabled={isSilentMode}
              onClick={isSilentMode ? undefined : alarmModal}
            />
          </SButtonContainer>
        </>
      )}
      {activeView === 'ACTIVE' && (
        <SFullScreenContainer>
          <PhoneWarningIcon />
          <STitle>{t('test_alarm_close_app')}</STitle>
          <SDescriptionText>
            {t('test_alarm_triggered')}
            <SDescriptionText className="secondsCount">{` ${remainingSeconds} `}</SDescriptionText>
            <SDescriptionText className="seconds">{t('test_alarm_seconds')}</SDescriptionText>
          </SDescriptionText>
        </SFullScreenContainer>
      )}
      {activeView === 'FINISHED' && (
        <SFullScreenContainer>
          <SirenIcon />
          <STitle>{t('test_alarm_receive_confirmation')}</STitle>
          <SDescriptionText>{t('test_alarm_receive_confirmation_desc')}</SDescriptionText>
          <SActionButtonsContainer>
            <SApproveButton tx="test_alarm_works" onClick={handleApprove} />
            <SDeclineButton tx="test_alarm_not_work" onClick={handleDecline} />
          </SActionButtonsContainer>
        </SFullScreenContainer>
      )}
      {activeView === 'NOT_RECEIVED' && (
        <SFullScreenContainer>
          <CogIcon />
          <STitle>{t('test_alarm_troubleshoot_title')}</STitle>
          <SDescriptionText>{t('test_alarm_troubleshoot_desktop_desc')}</SDescriptionText>
          <SActionButtonsContainer>
            <Button tx="test_alarm_troubleshoot" onClick={handleTroubleshoot} />
            <HomeButton tx="test_alarm_back_to_home" onClick={navigateToDashboard} />
          </SActionButtonsContainer>
        </SFullScreenContainer>
      )}
    </Page>
  );
};
